import { useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildSteps, TourStep } from './tourSteps';
import { useAppStore } from '@/stores/appStore';

export interface TourHookResult {
  isActive: boolean;
  currentStep: number;
  steps: TourStep[];
  targetRect: DOMRect | null;
  startTour: (role: string) => void;
  goNext: (userId?: string) => void;
  goBack: () => void;
  skipTour: (userId?: string) => void;
}

export function useTour(): TourHookResult {
  const navigate = useNavigate();
  const location = useLocation();

  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  // Refs so callbacks always have latest values without stale closures
  // and without calling side-effectful code inside React state updaters
  const stepsRef = useRef<TourStep[]>([]);
  const currentStepRef = useRef(0);

  const findElement = useCallback((tourId: string, initialDelay: number) => {
    const startTime = Date.now();
    const maxWait = 4000;
    const attempt = () => {
      const el = document.querySelector(`[data-tour-id="${tourId}"]`);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
        return;
      }
      if (Date.now() - startTime < maxWait) {
        setTimeout(attempt, 80);
      }
      // If still not found after maxWait, leave targetRect null (full overlay shown, no tooltip flash)
    };
    setTimeout(attempt, initialDelay);
  }, []);

  const activateStep = useCallback(
    (index: number) => {
      const step = stepsRef.current[index];
      if (!step) return;

      setTargetRect(null);

      if (!step.tourId) return; // welcome step — centered modal, no anchor

      if (step.openInitiativeModal) {
        const initiatives = useAppStore.getState().initiatives;
        if (initiatives.length > 0) {
          useAppStore.getState().openViewInitiativeModal(initiatives[0], 'intelligence');
          findElement(step.tourId, 450);
        }
        return;
      }

      // Close initiative modal if open when leaving intelligence step
      if (useAppStore.getState().initiativeModalOpen) {
        useAppStore.getState().closeInitiativeModal();
      }

      // Open user menu dropdown before looking for the target element
      if (step.openUserMenu) {
        const alreadyOpen = !!document.querySelector(`[data-tour-id="${step.tourId}"]`);
        if (!alreadyOpen) {
          const menuBtn = document.querySelector('[data-tour-id="user-menu-btn"]') as HTMLElement | null;
          if (menuBtn) menuBtn.click();
        }
        findElement(step.tourId, 150);
        return;
      }

      // Navigate if route or settings tab differs from current location
      const needsNav = step.route && (
        location.pathname !== step.route ||
        (step.settingsTab && !location.search.includes(`tab=${step.settingsTab}`))
      );

      if (needsNav) {
        const dest = step.settingsTab
          ? `${step.route}?tab=${step.settingsTab}`
          : step.route!;
        navigate(dest);
        findElement(step.tourId, 400);
      } else {
        findElement(step.tourId, 100);
      }
    },
    [location.pathname, navigate, findElement]
  );

  const completeTour = useCallback((userId?: string) => {
    setIsActive(false);
    setTargetRect(null);
    if (userId) localStorage.setItem(`tour_completed_${userId}`, '1');
    if (useAppStore.getState().initiativeModalOpen) {
      useAppStore.getState().closeInitiativeModal();
    }
  }, []);

  const startTour = useCallback((role: string) => {
    const filtered = buildSteps(role);
    stepsRef.current = filtered;
    currentStepRef.current = 0;
    setSteps(filtered);
    setCurrentStep(0);
    setTargetRect(null);
    setIsActive(true);
  }, []);

  const goNext = useCallback(
    (userId?: string) => {
      const next = currentStepRef.current + 1;
      if (next >= stepsRef.current.length) {
        completeTour(userId);
        return;
      }
      currentStepRef.current = next;
      setCurrentStep(next);
      activateStep(next);
    },
    [activateStep, completeTour]
  );

  const goBack = useCallback(() => {
    const prev = currentStepRef.current - 1;
    if (prev < 1) return; // don't go back to welcome
    currentStepRef.current = prev;
    setCurrentStep(prev);
    activateStep(prev);
  }, [activateStep]);

  const skipTour = useCallback(
    (userId?: string) => completeTour(userId),
    [completeTour]
  );

  return { isActive, currentStep, steps, targetRect, startTour, goNext, goBack, skipTour };
}
